import { Slot } from "@radix-ui/react-slot";
import { Menu } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { Sheet, SheetBody, SheetContent, SheetTitle, SheetTrigger } from "../molecules/sheet.js";

/**
 * Header — shared app-shell top bar.
 *
 * Renders the persistent header above page chrome (typically slotted into a
 * dashboard layout's `header` slot). Layout: brand block on the left,
 * free-form `actions` slot on the right.
 *
 * The header intentionally has no "platform name" / "context label" surface.
 * The active app surface is already communicated by the logo and by the
 * PageHeader page title. Do not reintroduce a platform-name label here.
 *
 * Composition:
 *   - `logo` — required brand mark. Pass the Cakewalk wordmark SVG. Use
 *     `logoAsChild` (Radix Slot) so the consumer can wrap it in a
 *     router-aware element.
 *   - `user` — convenience structured greeting: a "<prefix> <name>" line plus
 *     an optional smaller organization line below.
 *   - `greeting` — fully-custom greeting slot (wins over `user`).
 *   - `actions` — right-aligned action slot.
 */

export interface HeaderUser {
  /** Primary identification line. Typically a full name; falls back to email. */
  name: React.ReactNode;
  /** Optional secondary line — company / organization the user belongs to. */
  organization?: React.ReactNode;
  /** Optional prefix rendered before the name. Defaults to "Welcome". */
  prefix?: React.ReactNode;
}

export interface HeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Brand mark slot. Required for accessibility. */
  logo: React.ReactNode;
  /**
   * Render the brand mark via Radix Slot so the consumer can pass an `<a>`
   * or router-aware element that wraps the logo.
   */
  logoAsChild?: boolean;
  /** Accessible label applied to the logo wrapper. Default "Go to home". */
  logoLabel?: string;
  /**
   * Convenience structured greeting. Renders the user's name on the first
   * line and (optionally) their organization on a smaller line below.
   * Ignored when `greeting` is also provided.
   */
  user?: HeaderUser;
  /**
   * Fully-custom greeting slot. Takes precedence over `user` when both are
   * provided.
   */
  greeting?: React.ReactNode;
  /** Right-aligned action slot. */
  actions?: React.ReactNode;
  /** Mobile navigation content opened from the hamburger menu. */
  mobileNav?: React.ReactNode;
  /** Accessible label for the mobile menu trigger. Default "Open navigation". */
  mobileNavLabel?: string;
  /** Accessible title for the mobile navigation sheet. Default "Navigation". */
  mobileNavTitle?: React.ReactNode;
  /** Sticky positioning. Default false. */
  sticky?: boolean;
}

export const Header = React.forwardRef<HTMLElement, HeaderProps>(function Header(
  {
    actions,
    className,
    greeting,
    logo,
    logoAsChild = false,
    logoLabel = "Go to home",
    mobileNav,
    mobileNavLabel = "Open navigation",
    mobileNavTitle = "Navigation",
    sticky = false,
    user,
    ...props
  },
  ref,
) {
  const LogoComp = logoAsChild ? Slot : "span";
  const resolvedGreeting = greeting ?? (user ? <HeaderGreeting user={user} /> : null);

  return (
    <header
      ref={ref}
      className={cx(
        "cw-v2-header",
        sticky && "cw-v2-header--sticky",
        Boolean(mobileNav) && "cw-v2-header--has-mobile-nav",
        className,
      )}
      {...props}
    >
      <div className="cw-v2-header__inner">
        <div className="cw-v2-header__brand">
          <LogoComp className="cw-v2-header__logo" aria-label={logoLabel}>
            {logo}
          </LogoComp>
        </div>
        {resolvedGreeting || actions ? (
          <div className="cw-v2-header__end">
            {resolvedGreeting ? (
              <span className="cw-v2-header__greeting">{resolvedGreeting}</span>
            ) : null}
            {actions ? <div className="cw-v2-header__actions">{actions}</div> : null}
          </div>
        ) : null}
        {mobileNav ? (
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label={mobileNavLabel}
                className="cw-v2-header__mobile-menu"
                title={mobileNavLabel}
              >
                <Menu aria-hidden className="cw-v2-header__mobile-menu-icon" />
              </button>
            </SheetTrigger>
            <SheetContent className="cw-v2-header__mobile-nav-sheet" side="left" size="sm">
              <SheetTitle className="cw-v2-header__mobile-nav-title">{mobileNavTitle}</SheetTitle>
              <SheetBody className="cw-v2-header__mobile-nav-body">{mobileNav}</SheetBody>
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
    </header>
  );
});
Header.displayName = "Header";

function HeaderGreeting({ user }: { user: HeaderUser }) {
  const prefix = user.prefix ?? "Welcome";
  return (
    <span className="cw-v2-header__greeting-stack">
      <span className="cw-v2-header__greeting-line">
        {prefix} <span className="cw-v2-header__greeting-name">{user.name}</span>
      </span>
      {user.organization ? (
        <span className="cw-v2-header__greeting-org">{user.organization}</span>
      ) : null}
    </span>
  );
}
